import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for persisting filter state in sessionStorage
 * Filters persist across navigation but are cleared on browser close or logout
 *
 * @param {string} pageKey - Unique identifier for the page (e.g., 'purchase-orders', 'sales-orders')
 * @param {object} defaultFilters - Default filter values to use when no stored filters exist
 * @returns {[object, function, function]} - [filters, setFilters, clearFilters]
 */
const useFilterPersistence = (pageKey, defaultFilters = {}) => {
  // Helper to get username (not a hook, just a pure function)
  const getUsername = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.username || 'anonymous';
    } catch {
      return 'anonymous';
    }
  };

  // Helper to generate storage key (not a hook, just a pure function)
  const getStorageKey = () => {
    const username = getUsername();
    return `filters_${pageKey}_${username}`;
  };

  // Load initial filters from sessionStorage or use defaults
  const loadStoredFilters = () => {
    try {
      const storageKey = getStorageKey();
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all required keys exist
        return { ...defaultFilters, ...parsed };
      }
    } catch (error) {
      console.error('Error loading stored filters:', error);
    }
    return defaultFilters;
  };

  const [filters, setFiltersState] = useState(loadStoredFilters);

  // Save filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      const storageKey = getStorageKey();
      sessionStorage.setItem(storageKey, JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters to sessionStorage:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pageKey]);

  // Enhanced setFilters that accepts updater function or new value
  const setFilters = useCallback((updater) => {
    setFiltersState((prevFilters) => {
      const newFilters = typeof updater === 'function' ? updater(prevFilters) : updater;
      return newFilters;
    });
  }, []);

  // Clear filters and reset to defaults
  const clearFilters = useCallback(() => {
    try {
      const storageKey = getStorageKey();
      sessionStorage.removeItem(storageKey);
      setFiltersState(defaultFilters);
    } catch (error) {
      console.error('Error clearing filters:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  // Listen for logout event to clear filters
  useEffect(() => {
    const handleLogout = () => {
      clearFilters();
    };

    window.addEventListener('userLogout', handleLogout);
    return () => {
      window.removeEventListener('userLogout', handleLogout);
    };
  }, [clearFilters]);

  return [filters, setFilters, clearFilters];
};

export default useFilterPersistence;
